import {
  ContractCallBase,
  ContractDeployBase,
  STXTransferBase,
  TransactionTypes,
} from '@stacks/connect';
import {
  addressToString,
  AuthType,
  cvToValue,
  deserializeTransaction,
  PayloadType,
  serializeCV,
  StacksTransaction,
} from '@stacks/transactions';

import { ISignature, ITransaction, TransactionStatus } from '../store/types';

const MEMO_DESERIALIZATION_STUB = '\u0000';

const cleanMemoString = (memo: string): string => {
  return memo.replaceAll(MEMO_DESERIALIZATION_STUB, '');
};

export interface IMultisigTransaction {
  stacksTransaction: StacksTransaction;
  sponsored: boolean;
  nonce: number;
  fee: number | bigint;
  rawTransaction: string;
  txType: TransactionTypes;
  internalTxType: InternalTransactionType;
  payload: ContractDeployBase | ContractCallBase | STXTransferBase;
  txId?: string;
  _id: string;
  create_time: number;
  update_time: number;
  creator: string;
  status: TransactionStatus;
  errorReason?: string;
  type: 'SIGNATURE' | 'TRANSACTION';
  signatures: ISignature[];
  signedTransactionHex?: string;
  isSigningFlowTx?: boolean; // old tx format
  notes?: string;
}

type InternalTransactionType =
  | 'transfer-stx'
  | 'transfer-sip-009'
  | 'transfer-sip-010'
  | 'contract-call'
  | 'contract-deploy'
  | any;

function arrayBufferToHex(arrayBuffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(arrayBuffer), (byte) => byte.toString(16).padStart(2, '0')).join(
    ''
  );
}

export const transactionPayloadToMultisigTransaction = (
  transaction: ITransaction
): IMultisigTransaction => {
  const rawTransaction = transaction.rawTransactionHex;
  const stacksTransaction = deserializeTransaction(rawTransaction);

  // TODO why not just stacks transaction?
  const fee = transaction.signedTransactionHex
    ? deserializeTransaction(transaction.signedTransactionHex).auth.spendingCondition.fee
    : stacksTransaction.auth.spendingCondition.fee;

  const multisigTransaction = {
    stacksTransaction,
    sponsored: stacksTransaction.auth.authType === AuthType.Sponsored,
    nonce: Number(stacksTransaction.auth.spendingCondition.nonce),
    fee: fee,
    rawTransaction: rawTransaction,
    txId: transaction.txId,
    _id: transaction._id,
    create_time: transaction.create_time,
    update_time: transaction.update_time,
    creator: transaction.creator,
    status: transaction.status,
    signatures: transaction.signatures,
    isSigningFlowTx: 'signingFlows' in transaction,
    type: transaction.type,
    errorReason: transaction.error?.reason,
    notes: transaction.notes,
  } as Partial<IMultisigTransaction>;
  switch (stacksTransaction.payload.payloadType) {
    case PayloadType.VersionedSmartContract:
      multisigTransaction.txType = TransactionTypes.ContractDeploy;
      multisigTransaction.internalTxType = 'contract-deploy';
      multisigTransaction.payload = {
        contractName: stacksTransaction.payload.contractName.content,
        codeBody: stacksTransaction.payload.codeBody.content,
      };
      break;
    case PayloadType.TokenTransfer:
      multisigTransaction.txType = TransactionTypes.STXTransfer;
      multisigTransaction.internalTxType = 'transfer-stx';
      multisigTransaction.payload = {
        recipient: cvToValue(stacksTransaction.payload.recipient, true),
        amount: BigInt(stacksTransaction.payload.amount),
        memo: cleanMemoString(stacksTransaction.payload.memo.content),
      };
      break;
    case PayloadType.ContractCall:
      multisigTransaction.txType = TransactionTypes.ContractCall;
      multisigTransaction.internalTxType = 'contract-call';
      multisigTransaction.payload = {
        contractName: stacksTransaction.payload.contractName.content,
        contractAddress: addressToString(stacksTransaction.payload.contractAddress),
        functionName: stacksTransaction.payload.functionName.content,
        functionArgs: stacksTransaction.payload.functionArgs.map((arg) => {
          const serializedData = serializeCV(arg);

          return arrayBufferToHex(serializedData);
        }),
      };
      break;
    case PayloadType.SmartContract:
      multisigTransaction.txType = TransactionTypes.ContractDeploy;
      multisigTransaction.internalTxType = 'contract-deploy';
      multisigTransaction.payload = {
        contractName: stacksTransaction.payload.contractName.content,
        codeBody: stacksTransaction.payload.codeBody.content,
      };
      break;
    default:
      throw new Error('Unsupported tx type');
  }

  return multisigTransaction as IMultisigTransaction;
};
